import React, { FC, useRef, useState } from 'react'
import styles from './TwelveWeeks.module.scss'
import { BarsProps, GraphContentProps, ModalID, ModalProps, TwelveWeeksProps } from './TwelveWeeks.types'
import { Button } from 'components/Phantom/Button'
import { Img } from 'components/basic/Img'
import { CountUp } from 'components/CountUp'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { usePrevious } from 'components/_hooks/usePrevious'
import { FullScreenModal } from 'components/Phantom/_sections/ThisIsPod4Ultra'
import { A } from 'components/basic/A'
import { Type } from 'components/Type'

export const TwelveWeeks: FC<TwelveWeeksProps> = (props) => {
	const {} = props

	const [modal, setModal] = useState<ModalID | null>(null)
	const prevModal = usePrevious(modal)

	return (
		<section className={styles.container}>
			<h2 className={'vh'}>Participant studies</h2>
			<article>
				<GraphContent
					header={'Improved heart health and optimized recovery'}
					title={'After sleeping with the Pod for just one week, participants in a study saw changes of the magnitude you would expect from 12 weeks of strength training.'}
					bars={[
						{
							title: 'Increase in REM for higher quality sleep',
							maxWidthPercentage: 100,
							number: 25,
						},
						{
							title: 'Higher HRV for improved recovery',
							maxWidthPercentage: 68,
							number: 17,
						},
						{
							title: 'Lower sleeping heart rate for optimal health',
							maxWidthPercentage: 52,
							number: 13,
						},
					]}
					cta={{
						text: 'Read more here',
						href: '#',
					}}
					id={'strength-training'}
					modalClick={setModal}
					centerContent={
						<Img
							src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/bfcm_2024/basketball_bfcm.jpg'}
							alt={'A man playing basketball'}
							className={styles.runner_image}
							objectFit={'cover'}
						/>
					}
				/>
			</article>
			<article>
				<GraphContent
					header={'Sharpen your focus and cognition'}
					title={'After using the Pod for just two days, participants in a study experienced remarkable changes in overall executive function.'}
					bars={[
						{
							title: 'Increase in short-term memory for improved recall',
							maxWidthPercentage: 100,
							number: 17,
						},
						{
							title: 'Increase in executive function for better attention to detail',
							maxWidthPercentage: 76,
							number: 13,
						},
						{
							title: 'Increase in processing speed for faster information processing',
							maxWidthPercentage: 47,
							number: 8,
						},
					]}
					cta={{
						text: 'Read more here',
						href: '#',
					}}
					id={'executive-function'}
					modalClick={setModal}
					centerContent={
						<Img
							src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/bfcm_2024/yoga_bfcm.jpg'}
							alt={'A woman doing yoga while sitting on the Pod'}
							className={styles.bike_image}
							objectFit={'cover'}
						/>
					}
				/>
			</article>
			<article>
				<GraphContent
					header={'Boost your energy and wake up refreshed'}
					title={'When using the Pod for just one week, participants in a study reported significant changes to their energy levels from improved sleep.'}
					bars={[
						{
							title: 'Faster sleep onset to fall asleep faster',
							maxWidthPercentage: 100,
							number: 44,
						},
						{
							title: 'Higher daytime energy scores for more energy',
							maxWidthPercentage: 77,
							number: 34,
						},
						{
							title: 'Fewer sleep interruptions to stay asleep longer',
							maxWidthPercentage: 54,
							number: 24,
						},
					]}
					cta={{
						text: 'Read more here',
						href: '#',
					}}
					id={'energy'}
					modalClick={setModal}
					centerContent={
						<Img
							src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/bfcm_2024/lady-in-bed.png'}
							alt={'A woman sitting in bed lit with a morning sunrise'}
							className={styles.yoga_image}
							objectFit={'cover'}
						/>
					}
				/>
			</article>
			<Modal
				id={modal}
				prev={prevModal}
				closeModal={() => setModal(null)}
			/>
		</section>
	)
}

const GraphContent: FC<GraphContentProps> = (props) => {
	const { title, bars, cta, modalClick, id, header, centerContent } = props

	return (
		<div className={styles.graph_content}>
			<header>
				<Type.Headline2
					as={'h3'}
					animateOnScroll
				>
					{header}
				</Type.Headline2>
			</header>
			<div className={styles.graph_content_inner}>
				<div className={styles.graph_content_header}>
					<Type.Headline5>{title}</Type.Headline5>
					<Button.TextLink
						id={'strength-training-cta'}
						onClick={() => modalClick(id)}
						className={styles.cta}
					>
						{cta.text}
					</Button.TextLink>
				</div>
				<ul className={styles.bars}>
					{bars.map((bar) => (
						<li key={bar.title}>
							<Bar {...bar} />
						</li>
					))}
				</ul>
				<div className={styles.center_content}>{centerContent}</div>
			</div>
		</div>
	)
}

const Bar: FC<BarsProps> = (props) => {
	const { title, maxWidthPercentage, number } = props

	const [numberTimeline, setNumberTimeline] = useState<gsap.core.Timeline>()
	const numberRef = useRef(null)
	const barRef = useRef(null)

	useGSAP(
		() => {
			gsap.registerPlugin(ScrollTrigger)
			if (!numberTimeline) return

			const tl = gsap.timeline({
				ease: 'none',
				scrollTrigger: {
					trigger: barRef.current,
					start: `top 90%`,
					end: '+=200px',
					toggleActions: 'restart none none reverse',
				},
			})

			tl.fromTo(
				barRef.current,
				{
					opacity: 0,
				},
				{
					opacity: 1,
					duration: 0.25,
				},
				0
			)
			tl.add(numberTimeline, 0)
		},
		{
			dependencies: [numberTimeline],
			revertOnUpdate: true,
		}
	)

	return (
		<div
			className={styles.bar_container}
			ref={barRef}
		>
			<Type.Headline1
				className={styles.number}
				ref={numberRef}
			>
				<CountUp
					endValue={number}
					animationStyle={'in-place'}
					trigger={true}
					duration={1}
					onTimelineReady={setNumberTimeline}
				/>
				%
			</Type.Headline1>
			<Type.Body1 className={styles.bar_title}>{title}</Type.Body1>
		</div>
	)
}

const StrengthTrainingModal = () => {
	return (
		<article className={styles.modal_content}>
			<h2>Improved sleep and recovery with the Pod</h2>
			<p>
				In the first research study of its kind, our R&D team explored the impact of the Pod on sleep quality and cardiovascular recovery in 54 participants with more than 300 nights of home sleep
				test (HST) device data. Participants slept for one week with Pod temperatures off (Pod OFF), followed by one week with Pod temperatures on (Pod ON), and a final two nights with Pod OFF.
			</p>
			<h3>Sleeping on the Pod improved sleep and health metrics</h3>
			<p>The study revealed several exciting findings regarding the Pod’s impact, including:</p>
			<ul>
				<li>
					<b>Increased deep, light, and REM sleep</b>: With the Pod ON, men’s deep and light sleep increased by 14 min (+22%) in the first half of the night, and light sleep by 23 min (+19%) in the
					second half of the night. Women’s REM sleep increased by 9 min (+25%) in the first half of the night.
				</li>
				<li>
					<b>Enhanced cardiovascular recovery</b>: With Pod ON, participants saw cardiovascular changes of the magnitude you would expect with 12 weeks of exercise training. Average HR decreased by
					2%, and average HRV increased by 7%.
				</li>
				<li>
					<b>Improved perception of sleep quality</b>: Participants reported their sleep was significantly calmer, more comfortable, and that it was easier to fall asleep with the Pod ON vs. OFF.
				</li>
			</ul>
			{/*<table>*/}
			{/*	<thead>*/}
			{/*		<tr>*/}
			{/*			<th>Phase</th>*/}
			{/*			<th>Findings</th>*/}
			{/*		</tr>*/}
			{/*	</thead>*/}
			{/*	<tbody>*/}
			{/*		<tr>*/}
			{/*			<td>Bedtime Phase</td>*/}
			{/*			<td>*/}
			{/*				Women sleeping at warmer Pod temperatures <b>fell asleep approximately 7 minutes faster**</b>.*/}
			{/*			</td>*/}
			{/*		</tr>*/}
			{/*		<tr>*/}
			{/*			<td>Early Phase</td>*/}
			{/*			<td>*/}
			{/*				Men sleeping at cool temperatures experienced a 14-minute increase in deep sleep, corresponding to a <b>remarkable 22% improvement in deep sleep</b> during the first half of the night.*/}
			{/*				<br />*/}
			{/*				<br />*/}
			{/*				Women sleeping at cool temperatures enjoyed a 9-minute increase in REM sleep, resulting in a <b>significant 25% boost in REM sleep </b>during the first half of the night.**/}
			{/*			</td>*/}
			{/*		</tr>*/}
			{/*		<tr>*/}
			{/*			<td>Late Phase</td>*/}
			{/*			<td>*/}
			{/*				Men sleeping at warm temperatures experienced approximately 23 additional minutes of light sleep, equivalent to a <b>19% increase in light sleep </b>during the second half of the night.{' '}*/}
			{/*				<br />*/}
			{/*				<br />*/}
			{/*				Women sleeping at warmer temperatures had 8 minutes of increased wake time.**/}
			{/*			</td>*/}
			{/*		</tr>*/}
			{/*	</tbody>*/}
			{/*</table>*/}
			<h3>The Pod enhanced cardiovascular recovery</h3>
			<p>Using the Pod had a positive impact on cardiovascular recovery during sleep for both men and women:</p>
			<ul>
				<li>
					On average, participants experienced a statistically significant decrease with Pod ON of approximately 1 beat per minute (bpm) in HR, corresponding to a 2% decrease on average, with up to a
					13% decrease in sleeping HR.
				</li>
				<li>Heart rate variability (HRV) significantly increased by an average of 7% with Pod ON, with up to a 17% improvement in HRV.</li>
			</ul>
			<p>
				These cardiovascular improvements were transient and specifically induced by the Pod since they disappeared when participants turned off the Pod at the end of the study. Notably, these
				positive changes in HR and HRV were unrelated to changes in exercise, as there were no significant changes in exercise habits throughout the study.
			</p>
			<Button.TextLink
				id={'strength-ref-1'}
				href={'/blog/research-paper-reveals-the-eight-sleep-pod-improves-sleep/'}
			>
				Read more here
			</Button.TextLink>
			<div className={styles.modal_image_container}>
				<Img
					src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/twelve_weeks_modal_image.jpeg'}
					alt={'A woman reading a book while laying on the Pod 4 Ultra'}
					objectFit={'cover'}
				/>
			</div>
		</article>
	)
}

const ExecutiveFunctionModal = () => {
	return (
		<article className={styles.modal_content}>
			<h2>Increased cognitive function with the Pod</h2>
			<p>
				A good night’s sleep is crucial for maintaining health, emotional well-being, and overall wellness. Our prior studies have demonstrated that sleeping on the Eight Sleep Pod can improve deep
				sleep, total sleep time (TST), overall sleep quality, and cardiovascular recovery (
				<A
					id={'cognition-ref-1'}
					href={'/blog/hrv-hr-deep-sleep-improvement/'}
				>
					ref
				</A>
				,{' '}
				<A
					id={'cognition-ref-1'}
					href={'/blog/improved-sleep-quality/'}
				>
					ref
				</A>
				). Our latest research reveals an additional benefit of the Pod: it also enhances cognitive function. Overall, we found that after subjects slept on the Pod for 2 nights, they experienced:
			</p>
			<ul>
				<li>8% increase in processing speed</li>
				<li>9% increase in short-term memory recall speed</li>
				<li>13% increase in executive function</li>
			</ul>
			<h3>What are the different cognitive functions we measured?</h3>
			<p>We measured three main domains of cognitive function: processing speed, short-term memory, and executive function.</p>
			<ul>
				<li>
					Processing speed is the rate at which a person can perceive, understand, and respond to information. This could be important when trying to make decisions quickly, like when an athlete is
					deciding where to make a pass in a split second.
				</li>
				<li>
					Short-term memory is the capacity to remember information in a short period of time. For example, remembering a phone number long enough to dial it back indicates good short-term memory.
				</li>
				<li>
					Executive function involves planning, attention, remembering instructions, and multitasking. For example, effectively planning, delegating, and adjusting priorities for your team requires
					strong executive function.
				</li>
			</ul>
			<Button.TextLink
				id={'exec-ref-1'}
				href={'/blog/cognitive-function-improvement/'}
			>
				Read more here
			</Button.TextLink>
			<div className={styles.modal_image_container}>
				<Img
					src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/8Sleep_13_02_24122349-2+1_twelve_weeks_focus.jpg'}
					alt={'A man reading his phone while laying on the Pod 4 Ultra'}
					objectFit={'cover'}
				/>
			</div>
		</article>
	)
}

const EnergyModal = () => {
	return (
		<article className={styles.modal_content}>
			<h2>Improved energy with the Pod</h2>
			<p>
				Sleep quality is a critical pillar for happiness, productivity, and health. At Eight Sleep, we are committed to improving your sleep fitness through our dedication to scientific rigor and
				validation. We are excited to share the results of our latest research study, where we have found significant improvements in various sleep metrics from sleeping on the Eight Sleep Pod.{' '}
				<b>After one month of sleeping on the Pod, members reported 32% better sleep quality scores on average.</b>This overall improvement in sleep quality consists of improvements in each of the
				following components:
			</p>
			<ul>
				<li>34% higher daytime energy scores</li>
				<li>23% fewer sleep interruptions</li>
				<li>44% faster sleep onset</li>
			</ul>
			<p>These levels of sleep quality improvements are comparable to improvements from cognitive behavioral therapy for sleep.</p>
			<Button.TextLink
				id={'energy-ref-1'}
				href={'/blog/improved-sleep-quality/'}
			>
				Read more here
			</Button.TextLink>
			<div className={styles.modal_image_container}>
				<Img
					src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/8Sleep_13_02_24122349-2+1_twelve.jpg'}
					alt={'A man lying fast asleep on the Pod 4 Ultra.'}
					objectFit={'cover'}
				/>
			</div>
		</article>
	)
}

export const getModalContent = (id: ModalID) => {
	switch (id) {
		case 'strength-training':
			return <StrengthTrainingModal />
		case 'executive-function':
			return <ExecutiveFunctionModal />
		case 'energy':
			return <EnergyModal />
		default:
			return null
	}
}

const Modal: FC<ModalProps> = (props) => {
	const { id, prev, closeModal } = props

	return (
		<FullScreenModal
			closeAction={closeModal}
			visible={!!id}
			scrollable={true}
		>
			{getModalContent(id || prev)}
		</FullScreenModal>
	)
}
